import { useState } from "react";
import { KeyRound, Save, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4] pr-10";

export function AccountManager() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg" data-testid="account-manager">
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-5 w-5 text-[#1E63C4]" />
          <h3 className="font-heading font-bold text-lg">Change Password</h3>
        </div>
        <p className="text-sm text-[#555555] mb-5">Signed in as <strong>{user?.email}</strong>. Choose a strong password (at least 8 characters).</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Current password</label>
            <input type={show ? "text" : "password"} className={input} value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" autoComplete="current-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">New password</label>
            <div className="relative">
              <input type={show ? "text" : "password"} className={input} value={next} onChange={(e) => setNext(e.target.value)} required data-testid="new-password-input" autoComplete="new-password" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-[#999999] hover:text-[#1E63C4]" data-testid="toggle-password-visibility" aria-label="Toggle password visibility">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Confirm new password</label>
            <input type={show ? "text" : "password"} className={input} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input" autoComplete="new-password" />
          </div>
          <button type="submit" disabled={busy} data-testid="save-password-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
            <Save className="h-5 w-5" /> {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
